import React, {useCallback, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useClamped, useComponentProps} from 'helpers/hooks/utils';
import StyledMarkdown from 'components/atoms/Formatters/Markdown/Markdown.styles';
import remarkGfm from 'remark-gfm';
import rehypeRaw from "rehype-raw";
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import {withMemo} from 'helpers/wrapper';
import Typography from 'components/atoms/Text/Typography/Typography';
import Link from 'components/atoms/Links/Link/Link';
import utils from 'helpers/utils';
import ReactMarkdown, {defaultUrlTransform} from 'react-markdown';
import NavLink from 'components/atoms/Links/NavLink/NavLink';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Icon from 'components/atoms/Icons/Icon/Icon';

const Markdown = withMemo(React.forwardRef((props, ref) => {
  const {
    disabled,
    rehypePlugins,
    remarkPlugins,
    components,
    urlTransform,
    allowMentions,
    allowDataLinks,
    showTooltip,
    TooltipProps,
    isLoading,
    ...innerProps
  } = useComponentProps({
    allowDataLinks: true,
    ...props
  }, 'Markdown', {
    static: ['disabled'],
    styled: ['color']
  });

  const innerRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const alwaysTooltip = showTooltip === 'always';

  const [clamped, clampedProps] = useClamped(() => {
    return innerRef.current?.querySelector?.('.Markdown > *');
  }, {
    active: (!isLoading && showTooltip && !alwaysTooltip),
    onMouseOver: innerProps.onMouseOver,
    onTouchStart: innerProps.onTouchStart
  });

  const value = innerProps.children?.toString();

  const defaultComponents = useMemo(() => {
    const renderLink = ({node, ...props}) => {
      if (disabled) {
        props.tabIndex = -1;
      }

      const handleClick = (e) => {
        props.onClick?.(e);
        e.stopPropagation();
      }

      const isExternal = utils.isExternalLink(props.href);
      const content = (
        props.href && utils.isString(props.children) && (
          isExternal ? (utils.displayExternalLink(props.href) === utils.displayExternalLink(props.children)) :
            (utils.displayInternalLink(props.href) === utils.displayInternalLink(props.children))
        )
      ) ? (isExternal ? utils.displayExternalLink(props.children) : utils.displayInternalLink(props.children)) : props.children;

      const rendered = isExternal ?
        <Link {...props}
              onClick={handleClick}
              target="_blank">{content}</Link> :
        <NavLink {...props}
                 to={utils.cleanInternalLink(props.href, true)}
                 relative="path"
                 resetSearchParams={true}
                 onClick={handleClick}>{content}</NavLink>;

      if (components?.a) {
        return components.a({node, rendered, ...props});
      } else {
        return rendered;
      }
    }

    const renderIcon = ({node, ...props}) => {
      let rendered = <i {...props}>{props.children}</i>;
      const color = props.className?.includes('text-green') ? 'success' : (
        props.className?.includes('text-orange') ? 'warning' : 'error'
      );
      const icon = props.className?.includes('fa-times-circle') ? Cancel : (
        props.className?.includes('fa-times-check') ? CheckCircle : null
      )

      if (icon && color) {
        rendered = <Icon style={{fontSize: 'inherit', marginBottom: innerProps.theme.layout(-1.5)}}
                         color={color} icon={icon}/>;
      }

      if (components?.i) {
        return components.i({node, rendered, ...props});
      } else {
        return rendered;
      }
    }

    const renderEm = ({node, ...props}) => {
      const rendered = <em {...props}>{props.children}</em>;
      if (components?.em) {
        return components.em({node, rendered, ...props});
      } else {
        return rendered;
      }
    }

    return {
      a: renderLink,
      i: renderIcon,
      em: renderEm
    };
  }, [disabled, components, innerProps.theme]);

  const handleUrlTransform = useCallback((url) => {
    const transform = (url) => {
      if (allowDataLinks && utils.isDataLink(url)) {
        return url;
      } else if (allowMentions && url.startsWith('@')) {
        return url;
      } else {
        return defaultUrlTransform(url);
      }
    };

    return urlTransform ? urlTransform(url) : transform(url);
  }, [allowDataLinks, allowMentions, urlTransform]);

  const renderMarkdown = () => {
    return <ReactMarkdown skipHtml={true}
                          urlTransform={handleUrlTransform}
                          components={defaultComponents}
                          rehypePlugins={rehypePlugins ?? [rehypeRaw]}
                          remarkPlugins={remarkPlugins ?? [remarkGfm]}>
      {value}
    </ReactMarkdown>
  }

  const renderTooltip = () => {
    if (!isLoading && showTooltip && (alwaysTooltip || clamped)) {
      return <StyledMarkdown {...innerProps}>
        {renderMarkdown()}
      </StyledMarkdown>
    }
  }

  const render = (tooltip) => {
    if (isLoading) {
      return <Typography ref={innerRef} isLoading={true}>
        {value}
      </Typography>
    } else {
      return <StyledMarkdown ref={innerRef} {...innerProps}
                             {...(tooltip ? clampedProps : null)}>
        {renderMarkdown()}
      </StyledMarkdown>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  if (showTooltip && !utils.isEmpty(value)) {
    return <Tooltip title={renderTooltip()}
                    placement="bottom"
                    {...TooltipProps}>
      {render(true)}
    </Tooltip>
  } else {
    return render(false);
  }
}));

Markdown.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  showTooltip: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string
  ]),
  rehypePlugins: PropTypes.array,
  remarkPlugins: PropTypes.array,
  components: PropTypes.object,
  allowMentions: PropTypes.bool,
  allowDataLinks: PropTypes.bool,
  TooltipProps: PropTypes.object,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool
};

export default Markdown;
